<template>
    <v-form ref="form" autocomplete="off">
        <v-container fluid>
            <!-- SECCIÓN 1: PRODUCTOS DEVUELTOS -->
            <v-row>
                <label class="ml-2">
                    <h3 style="color: #f57c00;">
                        <v-icon color="orange darken-2">mdi-keyboard-return</v-icon>
                        {{ $t('devolucion.labels.seccionDevueltos') }}
                    </h3>
                </label>
            </v-row>
            <v-row>
                <devolucion-list
                    :items="itemsDevueltosParaTabla"
                    tipo="devueltos"
                    v-model="form"
                    ref="devolucionList"
                    @addProductToCart="addProductoDevuelto"
                    @delete="deleteProductoDevuelto"
                    @closeDialog="tableDialogClosed"
                />
            </v-row>

            <!-- SECCIÓN 2: PRODUCTOS NUEVOS (OPCIONAL) -->
            <v-row class="mt-6">
                <label class="ml-2">
                    <h3 style="color: #1976d2;">
                        <v-icon color="primary">mdi-cart-plus</v-icon>
                        {{ $t('devolucion.labels.seccionNuevos') }}
                    </h3>
                </label>
            </v-row>

            <v-row v-if="showListaPreciosSelector" align="center" class="px-2 mb-1">
                <v-col cols="12" md="4" lg="3" class="py-0">
                    <v-select
                        v-model="listaPreciosVentaId"
                        :items="listasPrecios"
                        item-text="nombre"
                        item-value="id"
                        outlined
                        dense
                        hide-details
                        clearable
                        label="Lista de precios"
                        placeholder="Lista default"
                        prepend-inner-icon="mdi-format-list-numbered"
                        @change="onListaPreciosChange"
                    />
                </v-col>
            </v-row>
            
            <v-row>
                <devolucion-list
                    :items="itemsNuevosParaTabla"
                    tipo="nuevos"
                    v-model="form"
                    ref="nuevosProductosList"
                    @addProductToCart="addProductoNuevo"
                    @delete="deleteProductoNuevo"
                    @closeDialog="tableDialogClosed"
                />
            </v-row>

            <!-- SECCIÓN 3: RESUMEN DE PAGO -->
            <v-row align="center" justify="center">
                <v-col cols="8" md="8" lg="6">
                    <v-card class="payment-panel mt-4">
                        <v-card-text>
                            <v-row>
                                <!-- COLUMNA IZQUIERDA -->
                                <v-col cols="12" md="6">
                                    <!-- Descuento -->
                                    <div class="info-card">
                                        <div class="field-label">
                                            <v-icon color="orange" small>mdi-tag-outline</v-icon>
                                            Descuento (%):
                                        </div>
                                        <v-text-field 
                                            v-model="descuento" 
                                            type="number" 
                                            outlined 
                                            dense
                                            :rules="descuentoRules"
                                        ></v-text-field>
                                    </div>

                                    <!-- Recargo -->
                                    <div class="info-card">
                                        <div class="field-label">
                                            <v-icon color="purple" small>mdi-plus-circle-outline</v-icon>
                                            Recargo (%):
                                        </div>
                                        <v-text-field 
                                            v-model="recargo" 
                                            type="number" 
                                            outlined 
                                            dense
                                            :rules="recargoRules"
                                        ></v-text-field>
                                    </div>

                                    <!-- Medios de Pago (múltiples con monto) -->
                                    <div class="info-card">
                                        <div class="field-label">
                                            <v-icon color="blue" small>mdi-credit-card-outline</v-icon>
                                            Medios de pago:
                                        </div>
                                        <div v-for="(item, index) in mediosDePago" :key="index" class="d-flex align-center mb-2">
                                            <v-select
                                                v-model="item.medio"
                                                :items="mediosPago"
                                                outlined
                                                dense
                                                hide-details
                                                class="flex-grow-1 mr-1"
                                                style="min-width: 0;"
                                            ></v-select>
                                            <v-text-field
                                                v-if="mediosDePago.length > 1"
                                                v-model.number="item.monto"
                                                type="number"
                                                prefix="$"
                                                outlined
                                                dense
                                                hide-details
                                                class="flex-grow-1 mr-1"
                                                style="max-width: 120px;"
                                                min="0"
                                                step="0.01"
                                                @input="emitMediosDePago"
                                            ></v-text-field>
                                            <v-btn
                                                v-if="mediosDePago.length > 1"
                                                icon
                                                small
                                                color="error"
                                                @click="quitarMedioDePago(index)"
                                            >
                                                <v-icon small>mdi-minus</v-icon>
                                            </v-btn>
                                        </div>
                                        <v-btn small text color="primary" @click="agregarMedioDePago" class="mt-1">
                                            <v-icon small left>mdi-plus</v-icon>
                                            Agregar medio de pago
                                        </v-btn>
                                    </div>
                                </v-col>

                                <!-- COLUMNA DERECHA -->
                                <v-col cols="12" md="6">
                                    <!-- Monto Devolución -->
                                    <div class="refund-card">
                                        <div class="refund-label">
                                            <v-icon color="orange darken-2" small>mdi-cash-refund</v-icon>
                                            {{ $t('devolucion.labels.montoDevolucion') }}
                                        </div>
                                        <div class="refund-amount">
                                            ${{ montoDevolucion.toFixed(2) }}
                                        </div>
                                    </div>

                                    <!-- Total por productos nuevos -->
                                    <div class="total-card">
                                        <div class="total-label">Total por productos nuevos</div>
                                        <div class="total-amount">${{ montoNuevosFinal.toFixed(2) }}</div>
                                    </div>
                                    
                                    <!-- Diferencia Final -->
                                    <div :class="diferenciaCardClass">
                                        <div class="change-label">
                                            <v-icon>{{ diferenciaIcon }}</v-icon>
                                            {{ diferenciaLabel }}
                                        </div>
                                        <div class="change-amount" :class="{'has-change': diferencia !== 0}">
                                            ${{ Math.abs(diferencia).toFixed(2) }}
                                        </div>
                                    </div>

                                    <!-- Dinero recibido (por defecto suma de medios; se puede editar; solo si hay diferencia a pagar) -->
                                    <div class="info-card" v-if="diferencia > 0">
                                        <div class="field-label">
                                            <v-icon color="green" small>mdi-cash</v-icon>
                                            Dinero recibido:
                                        </div>
                                        <v-text-field
                                            v-model.number="dineroRecibido"
                                            type="number"
                                            outlined
                                            dense
                                            hide-details
                                            prefix="$"
                                            min="0"
                                            step="0.01"
                                            @input="emitPagoRecibido"
                                        ></v-text-field>
                                    </div>

                                    <!-- Cambio a Devolver (solo si hay diferencia a pagar) -->
                                    <div class="change-card" v-if="diferencia > 0">
                                        <div class="change-label">
                                            <v-icon>mdi-minus-circle-outline</v-icon>
                                            Cambio a Devolver
                                        </div>
                                        <div class="change-amount" :class="{ 'has-change': cambioADevolver > 0 }">
                                            ${{ cambioADevolver.toFixed(2) }}
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>

<script>
import { InputErrorsByProps, RequiredRule } from '@dracul/common-frontend'
import DevolucionList from "../DevolucionList";
import OfertaProvider from "../../../../oferta/providers/OfertaProvider";
import { aplicarOfertasACarritoPreview } from "../../../../oferta/utils/ofertaCalculo";
import UserConfigurationProvider from "../../../../user-configuration/providers/UserConfigurationProvider";
import ListaPreciosVentaProvider from "../../../../lista-precios-venta/providers/ListaPreciosVentaProvider";

export default {
    name: "DevolucionForm",
    mixins: [InputErrorsByProps, RequiredRule],
    components: { DevolucionList },
    props: {
        value: {
            type: Object,
            required: true
        },
    },
    data() {
        return {
            loading: false,
            itemsDevueltos: [],
            itemsNuevos: [],
            descuento: 0,
            recargo: 0,
            mediosDePago: [{ medio: 'Efectivo', monto: 0 }],
            mediosPago: ['Efectivo', 'Tarjeta Débito', 'Tarjeta Crédito', 'Transferencia'],
            dineroRecibido: 0,
            descuentoRules: [
                (v) => parseFloat(v) >= 0 && parseFloat(v) <= 100 || "El descuento debe ser entre 0% y 100%",
            ],
            recargoRules: [
                (v) => parseFloat(v) >= 0 && parseFloat(v) <= 100 || "El recargo debe ser entre 0% y 100%",
            ],
            ofertasAplicables: [],
            loadingOfertas: false,
            isCalculatingOfertas: false, // Bandera para prevenir loops infinitos
            listasPrecios: [],
            listaPreciosVentaId: null,
        }
    },
    created() {
        ListaPreciosVentaProvider.list()
            .then((r) => {
                this.listasPrecios = (r && r.data && r.data.listaPreciosVentaList) || [];
                if (this.listasPrecios.length === 1) {
                    this.listaPreciosVentaId = this.listasPrecios[0].id;
                    this.$emit('actualizarListaPreciosVenta', this.listasPrecios[0].id);
                } else if (!this.listaPreciosVentaId) {
                    const def = this.listasPrecios.find((l) => l.isDefault);
                    const selected = def || this.listasPrecios[0];
                    if (selected) {
                        this.listaPreciosVentaId = selected.id;
                        this.$emit('actualizarListaPreciosVenta', selected.id);
                    }
                }
            })
            .catch(() => { this.listasPrecios = []; });
    },
    computed: {
        form: {
            get() { return this.value },
            set(val) { this.$emit('input', val) }
        },
        showListaPreciosSelector() {
            return this.listasPrecios.length > 1;
        },
        itemsDevueltosParaTabla() {
            return this.itemsDevueltos.map((item) => ({
                id: item.producto.id,
                nombre: item.producto.nombre || null,
                marca: item.producto.marca.nombre,
                modelo: item.producto.modelo.nombre,
                tamanio: item.producto.tamanio?.tamanio || '-',
                precio: item.producto.precio ?? item.producto.precioVenta,
                cantidad: item.cantidad
            }))
        },
        itemsNuevosParaTabla() {
            // Forzar evaluación de montoNuevosProductos primero para que aplique
            // ofertaAplicada a cada ítem antes de mapear (evita que la columna oferta
            // no se muestre al agregar el primer producto)
            void this.montoNuevosProductos;
            return this.itemsNuevos.map((item) => {
                
                const precioBase = item.producto.precio ?? item.producto.precioVenta;
                let precioFinal = precioBase;
                
                // Si hay oferta aplicada, calcular el precio con descuento
                if (item.ofertaAplicada && item.ofertaAplicada.descuento > 0) {
                    const descuentoPorUnidad = item.ofertaAplicada.descuento / item.cantidad;
                    precioFinal = precioBase - descuentoPorUnidad;
                }
                
                return {
                    id: item.producto.id,
                    nombre: item.producto.nombre || null,
                    stock: item.producto.stock,
                    marca: item.producto.marca.nombre,
                    modelo: item.producto.modelo.nombre,
                    tamanio: item.producto.tamanio?.tamanio || '-',
                    precio: precioFinal,
                    precioBase: precioBase,
                    cantidad: item.cantidad,
                    ofertaAplicada: item.ofertaAplicada || null
                }
            })
        },
        totalDescuentoOfertas() {
            let total = 0;
            for (const item of this.itemsNuevos) {
                if (item.ofertaAplicada && item.ofertaAplicada.descuento) {
                    total += item.ofertaAplicada.descuento;
                }
            }
            return total;
        },
        cantidadProductosConOferta() {
            return this.itemsNuevos.filter(item => item.ofertaAplicada).length;
        },
        montoDevolucion() {
            let total = 0;
            this.itemsDevueltos.forEach((item) => {
                total += ((item.producto.precio ?? item.producto.precioVenta ?? 0) * item.cantidad);
            });
            return total;
        },
        montoNuevosProductos() {
            let total = 0;

            if (!this.itemsNuevos || this.itemsNuevos.length === 0) {
                return 0;
            }

            const itemsParaCalculo = this.itemsNuevos.map((item) => ({
                producto: item.producto,
                cantidad: item.cantidad || 0,
                precioBase: (item.producto.precio ?? item.producto.precioVenta) || 0,
            }));
            const resultados = aplicarOfertasACarritoPreview(
                itemsParaCalculo,
                this.ofertasAplicables || []
            );

            for (let i = 0; i < this.itemsNuevos.length; i++) {
                const item = this.itemsNuevos[i];
                const res = resultados[i] || { precioTotal: 0, ofertaAplicada: null };
                this.$set(item, "ofertaAplicada", res.ofertaAplicada || null);
                total += res.precioTotal || 0;
            }

            return total;
        },
        montoNuevosFinal() {
            let total = this.montoNuevosProductos;
            // Aplicar descuento
            if (this.descuento && this.descuento > 0) {
                total = total * (1 - (this.descuento / 100));
            }
            // Aplicar recargo
            if (this.recargo && this.recargo > 0) {
                total = total * (1 + (this.recargo / 100));
            }
            return total;
        },
        diferencia() {
            return this.montoNuevosFinal - this.montoDevolucion;
        },
        diferenciaCardClass() {
            if (this.diferencia > 0) {
                return 'change-card payment-required';
            } else if (this.diferencia < 0) {
                return 'change-card refund-required';
            } else {
                return 'change-card';
            }
        },
        diferenciaIcon() {
            if (this.diferencia > 0) {
                return 'mdi-cash-plus';
            } else if (this.diferencia < 0) {
                return 'mdi-cash-refund';
            } else {
                return 'mdi-equal';
            }
        },
        diferenciaLabel() {
            if (this.diferencia > 0) {
                return this.$t('devolucion.labels.clientePaga');
            } else if (this.diferencia < 0) {
                return this.$t('devolucion.labels.clienteRecibe');
            } else {
                return this.$t('devolucion.labels.intercambioSinDinero');
            }
        },
        cambioADevolver() {
            // Solo calcular cambio si hay diferencia positiva (cliente debe pagar)
            if (this.diferencia > 0) {
                const diferenciaNumero = Number(this.diferencia);
                const recibido = Number(this.dineroRecibido) || 0;
                const cambio = recibido - diferenciaNumero;
                return cambio > 0 ? cambio : 0;
            }
            return 0;
        },
        medioPagoPrincipal() {
            const conMonto = (this.mediosDePago || []).filter((p) => Number(p.monto) > 0);
            if (conMonto.length === 0) return 'Efectivo';
            const mayor = conMonto.reduce((a, b) => (Number(b.monto) >= Number(a.monto) ? b : a), conMonto[0]);
            return mayor ? mayor.medio : 'Efectivo';
        },
        sumaMediosDePago() {
            if (!this.mediosDePago || !this.mediosDePago.length) return 0;
            return this.mediosDePago.reduce((acc, p) => acc + (Number(p.monto) || 0), 0);
        }
    },
    watch: {
        itemsDevueltos: {
            handler() {
                this.$emit('updateProductosDevueltos', this.itemsDevueltos);
            },
            deep: true
        },
        itemsNuevos: {
            handler() {
                this.$emit('updateProductosNuevos', this.itemsNuevos);
                // Recalcular ofertas cuando cambian los productos nuevos
                // Prevenir loop infinito: no ejecutar si ya estamos calculando
                if (this.itemsNuevos.length > 0 && !this.isCalculatingOfertas) {
                    this.calcularOfertasAplicables();
                }
            },
            deep: true
        },
        descuento(newVal) {
            const value = newVal === '' || newVal == null ? 0 : parseFloat(newVal);
            this.$emit('actualizarDescuento', value);
        },
        recargo(newVal) {
            const value = newVal === '' || newVal == null ? 0 : parseFloat(newVal);
            this.$emit('actualizarRecargo', value);
        },
        mediosDePago: {
            handler() {
                this.emitMediosDePago();
                this.dineroRecibido = this.sumaMediosDePago;
                this.emitPagoRecibido();
                if (this.itemsNuevos.length > 0) {
                    this.calcularOfertasAplicables();
                }
            },
            deep: true
        },
        diferencia(newVal) {
            if (newVal <= 0) this.dineroRecibido = 0;
            this.emitPagoRecibido();
        }
    },
    methods: {
        onListaPreciosChange(val) {
            this.$emit('actualizarListaPreciosVenta', val || null);
            this.ofertasAplicables = [];
        },
        addProductoDevuelto(item) {
            const existingItem = this.itemsDevueltos.find((itemCarrito) => {
                return itemCarrito.producto.id === item.producto.id
            });
            const cantidadNueva = item.cantidad || 1;

            if (existingItem) {
                // Si el producto ya existe en el carrito, incrementar la cantidad
                existingItem.cantidad += cantidadNueva;
            } else {
                // Si no existe, agregarlo al carrito
                this.itemsDevueltos.push(item);
            }
        },
        deleteProductoDevuelto(item) {
            const index = this.itemsDevueltos.map(itemCarrito => itemCarrito.producto.id).indexOf(item.id);
            if (index > -1) {
                // Guardar el item original antes de eliminarlo para pasarlo al evento
                this.itemsDevueltos.splice(index, 1);
                // El item original tiene la estructura { producto: {...}, cantidad: ... }
            }
        },
        addProductoNuevo(item) {
            const existingItem = this.itemsNuevos.find((itemCarrito) => {
                return itemCarrito.producto.id === item.producto.id
            });
            const cantidadNueva = item.cantidad || 1;
            const stockDisponible = item.producto.stock;
            const cantidadActual = existingItem ? existingItem.cantidad : 0;

            // Validar que no se supere el stock disponible
            if (cantidadActual + cantidadNueva > stockDisponible) {
                return;
            }

            if (existingItem) {
                // Si el producto ya existe en el carrito, incrementar la cantidad
                existingItem.cantidad += cantidadNueva;
            } else {
                // Si no existe, agregarlo al carrito
                this.itemsNuevos.push(item);
            }
        },
        deleteProductoNuevo(item) {
            const index = this.itemsNuevos.map(itemCarrito => itemCarrito.producto.id).indexOf(item.id);
            if (index > -1) {
                // Guardar el item original antes de eliminarlo para pasarlo al evento
                this.itemsNuevos.splice(index, 1);
                // El item original tiene la estructura { producto: {...}, cantidad: ... }
            }
        },
        tableDialogClosed() {
            // Cerrar diálogo de búsqueda
        },
        agregarMedioDePago() {
            this.mediosDePago.push({ medio: 'Efectivo', monto: 0 });
        },
        quitarMedioDePago(index) {
            if (this.mediosDePago.length > 1) {
                this.mediosDePago.splice(index, 1);
                this.emitMediosDePago();
            }
        },
        emitMediosDePago() {
            const list = (this.mediosDePago || []).map((p) => ({
                medio: p.medio || 'Efectivo',
                monto: Number(p.monto) || 0
            }));
            this.$emit('actualizarMediosDePago', list);
        },
        emitPagoRecibido() {
            this.$emit('actualizarPagoRecibido', {
                dineroRecibido: Number(this.dineroRecibido) || 0,
                cambioADevolver: this.cambioADevolver
            });
        },
        async calcularOfertasAplicables() {
            // Prevenir loop infinito: si ya estamos calculando, salir
            if (this.isCalculatingOfertas) {
                console.log('⚠️ Ya estamos calculando ofertas, evitando loop');
                return;
            }
            
            if (this.itemsNuevos.length === 0) {
                this.ofertasAplicables = [];
                return;
            }
            
            this.isCalculatingOfertas = true;
            
            try {
                this.loadingOfertas = true;
                const depositoId = UserConfigurationProvider.getDepositoIdFromCache();
                
                if (!depositoId) {
                    this.ofertasAplicables = [];
                    return;
                }
                
                // Preparar itemsCarrito para la query
                const itemsCarritoInput = this.itemsNuevos.map(item => ({
                    producto: item.producto.id,
                    cantidad: item.cantidad || 0,
                    precioBase: item.producto.precio || item.producto.precioVenta || 0
                }));
                
                const fecha = new Date().toISOString();
                console.log('=== CALCULAR OFERTAS (DEVOLUCIÓN) ===');
                console.log('Medio de pago principal:', this.medioPagoPrincipal);
                console.log('Items nuevos:', this.itemsNuevos);
                console.log('Depósito ID:', depositoId);
                
                const response = await OfertaProvider.getOfertasAplicables(
                    itemsCarritoInput,
                    depositoId,
                    this.medioPagoPrincipal || null,
                    fecha
                );
                
                console.log('Response completo:', response);
                console.log('Ofertas recibidas del backend:', response?.data?.ofertasAplicablesMovimiento);
                
                if (response && response.data && response.data.ofertasAplicablesMovimiento) {
                    this.ofertasAplicables = response.data.ofertasAplicablesMovimiento;
                    console.log('Ofertas aplicables asignadas:', this.ofertasAplicables);
                } else {
                    this.ofertasAplicables = [];
                    console.log('No hay ofertas aplicables');
                }
            } catch (error) {
                console.error('Error obteniendo ofertas aplicables:', error);
                this.ofertasAplicables = [];
            } finally {
                this.loadingOfertas = false;
                this.isCalculatingOfertas = false;
            }
        }
    }
}
</script>

<style scoped>
.payment-panel {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    padding: 20px;
    border-radius: 12px;
}

.info-card {
    background: white;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.field-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
}

.refund-card {
    background: linear-gradient(135deg, #ff9800 0%, #ffa726 100%);
    padding: 20px;
    border-radius: 12px;
    text-align: center;
    margin-bottom: 16px;
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
}

.refund-label {
    color: white;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.refund-amount {
    color: white;
    font-size: 36px;
    font-weight: bold;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.subtotal-amount {
    font-size: 24px;
    font-weight: bold;
    color: #1976d2;
    text-align: right;
}

.change-card {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
    text-align: center;
    border: 2px solid #e0e0e0;
}

.change-card.payment-required {
    background: #ffebee;
    border-color: #f44336;
}

.change-card.refund-required {
    background: #e8f5e9;
    border-color: #4caf50;
}

.change-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #666;
    margin-bottom: 8px;
}

.change-amount {
    font-size: 42px;
    font-weight: bold;
    color: #9e9e9e;
}

.change-amount.has-change {
    color: #333;
}

.payment-required .change-amount.has-change {
    color: #f44336;
}

.refund-required .change-amount.has-change {
    color: #4caf50;
}

.total-card {
    background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
    padding: 24px;
    border-radius: 12px;
    text-align: center;
    margin-bottom: 16px;
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.total-label {
    color: white;
    font-size: 16px;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: 8px;
}

.total-amount {
    color: white;
    font-size: 48px;
    font-weight: bold;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

/* Alerta de ofertas */
.ofertas-alert {
    animation: pulseGlow 2s ease-in-out infinite;
    border-left-width: 6px !important;
}

@keyframes pulseGlow {
    0%, 100% {
        box-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
    }
    50% {
        box-shadow: 0 0 20px rgba(76, 175, 80, 0.6);
    }
}
</style>
